<template>
    <div class="bill-verification">
        <!-- 헤더 -->
        <div class="content-header">
            <h3>
                <i class="fas fa-check-circle"></i>
                요금 계산 검증
            </h3>
            <!-- 검증 실행 폼 -->
            <div class="verify-form-header">
                <div class="form-field-inline">
                    <label>계약 선택 <span class="required">*</span></label>
                    <select v-model.number="verifyParams.contractIdx" class="form-select-header contract-select-wide">
                        <option value="">선택하세요</option>
                        <option v-for="contract in contracts" :key="contract.contractIdx" :value="contract.contractIdx">
                            {{ getContractDisplayText(contract) }}
                        </option>
                    </select>
                </div>

                <div class="form-field-inline">
                    <label>집계년월 <span class="required">*</span></label>
                    <div
                        class="date-picker-container-header"
                        v-click-outside="closeDatePicker"
                        @click="toggleDatePicker"
                    >
                        <div class="date-display-header">
                            <span>{{ dateDisplay }}</span>
                            <i :class="['fas fa-chevron-down', { rotate: showDatePicker }]"></i>
                        </div>

                        <!-- 드롭다운 -->
                        <div v-if="showDatePicker" class="date-dropdown">
                            <div class="year-selector">
                                <button @click.stop="changeYear(-1)">
                                    <i class="fas fa-chevron-left"></i>
                                </button>
                                <span>{{ dateYear }}</span>
                                <button @click.stop="changeYear(1)">
                                    <i class="fas fa-chevron-right"></i>
                                </button>
                            </div>
                            <div class="month-grid">
                                <button
                                    v-for="month in 12"
                                    :key="month"
                                    :class="['month-btn', { active: month === dateMonth }]"
                                    @click.stop="selectMonth(month)"
                                >
                                    {{ month }}월
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form-field-inline">
                    <button class="btn-verify-header" @click="executeVerification" :disabled="verifying">
                        <i :class="['fas', verifying ? 'fa-spinner fa-spin' : 'fa-search']"></i>
                        {{ verifying ? "검증 중..." : "검증 실행" }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 2컬럼 레이아웃: 왼쪽(요약) + 오른쪽(상세) -->
        <div class="verification-layout">
            <!-- 왼쪽 영역: 요금 계산 정보 -->
            <div class="left-panel">
                <!-- 기본 정보 -->
                <div v-if="verifyResult" class="info-section-compact">
                    <h6 class="section-title-compact">요금 정보</h6>
                    <div class="info-grid-compact">
                        <div class="info-row-compact">
                            <span class="info-label-compact">계약명</span>
                            <span class="info-value-compact">{{ verifyResult.contract?.contractName || "-" }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">공급자</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.supplier?.supplierName || "-"
                            }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">요금제</span>
                            <span class="info-value-compact">{{ verifyResult.contract?.cost?.costName || "-" }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">기본요금 산정</span>
                            <span class="info-value-compact">{{
                                getBaseFeeCalcMethod(verifyResult.contract?.cost)
                            }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">사용료 산정</span>
                            <span class="info-value-compact">{{
                                getUsageFeeCalcMethod(verifyResult.contract?.cost)
                            }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">계약 시작일</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.startDate
                                    ? formatDate(verifyResult.contract.startDate)
                                    : "미지정"
                            }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">계약 종료일</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.endDate ? formatDate(verifyResult.contract.endDate) : "미지정"
                            }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">검침일</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.readingDay ? verifyResult.contract.readingDay + "일" : "미입력"
                            }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">집계년월</span>
                            <span class="info-value-compact">{{ formatYearMonth(verifyResult.regYm) }}</span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">사용 데이터 기간</span>
                            <span class="info-value-compact"
                                >{{ formatDate(verifyResult.usage?.calculationPeriod?.startDate) }} ~
                                {{ formatDate(verifyResult.usage?.calculationPeriod?.endDate) }}</span
                            >
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">데이터 수(시간)</span>
                            <span class="info-value-compact">
                                {{ getHourlyDataDisplay(verifyResult.usage) }}
                            </span>
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">계측량</span>
                            <span class="info-value-compact"
                                >{{ formatNumber(verifyResult.usage?.rawUsage, 0) }}
                                {{ verifyResult.usage?.rawUnit || "-" }}</span
                            >
                        </div>
                        <div class="info-row-compact">
                            <span class="info-label-compact">과금 사용량</span>
                            <span class="info-value-compact"
                                >{{ formatNumber(verifyResult.usage?.billingUsage, 0) }}
                                {{ verifyResult.usage?.billingUnit || "-" }}</span
                            >
                        </div>
                        <!-- Demand/PeakLinked일 때만 표시 (가스는 PeakLinked 제외) -->
                        <div v-if="shouldShowDemandFields(verifyResult)" class="info-row-compact">
                            <span class="info-label-compact">계약 용량</span>
                            <span class="info-value-compact"
                                >{{ formatNumber(verifyResult.contract?.contractCapacity || 0, 2) }} kW</span
                            >
                        </div>
                        <div v-if="shouldShowDemandFields(verifyResult)" class="info-row-compact">
                            <span class="info-label-compact">최대 수요 제한</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.maxDemandLimit
                                    ? formatNumber(verifyResult.contract.maxDemandLimit, 2) + " kW"
                                    : "미설정"
                            }}</span>
                        </div>
                        <div v-if="shouldShowDemandFields(verifyResult)" class="info-row-compact">
                            <span class="info-label-compact">한전 고객번호</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.kepcoCustNo || "선택 안함"
                            }}</span>
                        </div>
                        <div v-if="shouldShowPeakFields(verifyResult)" class="info-row-compact">
                            <span class="info-label-compact">피크 최대수요전력</span>
                            <span class="info-value-compact">{{
                                verifyResult.usage?.applyPeak
                                    ? formatNumber(verifyResult.usage.applyPeak, 2) + " kW"
                                    : "미입력"
                            }}</span>
                        </div>
                        <div v-if="shouldShowDemandFields(verifyResult)" class="info-row-compact">
                            <span class="info-label-compact">최대 수요</span>
                            <span class="info-value-compact"
                                >{{ formatNumber(verifyResult.usage?.maxDemand, 0) }}
                                {{ verifyResult.usage?.rawUnit || "-" }}
                            </span>
                        </div>
                        <!-- GAS 에너지 유형일 때만 표시 -->
                        <div v-if="isGasEnergyType(verifyResult.contract?.supplier)" class="info-row-compact">
                            <span class="info-label-compact">보정계수</span>
                            <span class="info-value-compact">{{
                                formatNumber(verifyResult.contract?.correctionFactor || 1, 3)
                            }}</span>
                        </div>
                        <div v-if="isGasEnergyType(verifyResult.contract?.supplier)" class="info-row-compact">
                            <span class="info-label-compact">환산계수</span>
                            <span class="info-value-compact">{{
                                verifyResult.contract?.conversionFactor
                                    ? formatNumber(verifyResult.contract.conversionFactor, 3)
                                    : "미입력"
                            }}</span>
                        </div>
                    </div>
                </div>

                <!-- 요금 요약 테이블 -->
                <div v-if="verifyResult" class="fee-summary-table-compact">
                    <table class="fee-table-compact">
                        <tbody>
                            <tr>
                                <td class="fee-label-compact">기본 요금</td>
                                <td class="fee-value-compact">
                                    {{ formatNumber(verifyResult.baseFeeCalculation?.result, 0) }} 원
                                </td>
                            </tr>
                            <tr>
                                <td class="fee-label-compact">사용량 요금</td>
                                <td class="fee-value-compact">
                                    {{ formatNumber(verifyResult.usageFeeCalculation?.result, 0) }} 원
                                </td>
                            </tr>
                            <tr>
                                <td class="fee-label-compact">기타 요금</td>
                                <td class="fee-value-compact">
                                    {{ formatNumber(verifyResult.otherFeesCalculation?.totalAmount, 0) }} 원
                                </td>
                            </tr>
                            <tr>
                                <td class="fee-label-compact">부가세</td>
                                <td class="fee-value-compact">
                                    {{ formatNumber(verifyResult.vatCalculation?.result, 0) }} 원
                                </td>
                            </tr>
                            <tr>
                                <td class="fee-label-compact">할인</td>
                                <td class="fee-value-compact discount-compact">
                                    -{{ formatNumber(verifyResult.discountCalculation?.result, 0) }} 원
                                </td>
                            </tr>
                            <tr class="fee-total-row-compact">
                                <td class="fee-label-compact">요금 합계</td>
                                <td class="fee-value-compact total-compact">
                                    {{ formatNumber(verifyResult.finalCalculation?.result, 0) }} 원
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <!-- Placeholder: 검증 실행 전 -->
                <div v-else class="placeholder-section">
                    <div class="placeholder-content">
                        <i class="fas fa-calculator"></i>
                        <p>계약과 집계년월을 선택한 후<br />검증 실행 버튼을 눌러주세요.</p>
                    </div>
                </div>
            </div>

            <!-- 오른쪽 영역: 요금 계산 상세 -->
            <div class="right-panel">
                <div v-if="verifyResult" class="verification-result">
                    <!-- 최대 수요 검증 경고 메시지 -->
                    <div
                        v-if="
                            verifyResult.usage?.rawData?.maxDemandValidation &&
                            !verifyResult.usage.rawData.maxDemandValidation.isValid &&
                            verifyResult.usage.rawData.maxDemandValidation.warnings &&
                            verifyResult.usage.rawData.maxDemandValidation.warnings.length > 0
                        "
                        class="max-demand-warning-compact"
                    >
                        <i class="fas fa-exclamation-triangle"></i>
                        <div class="warning-content-compact">
                            <div
                                v-for="(warning, idx) in verifyResult.usage.rawData.maxDemandValidation.warnings"
                                :key="idx"
                                class="warning-item-compact"
                            >
                                {{ `${warning.message} 아래의 계산된 요금은 최대 수요로 계산시 예상되는 금액입니다.` }}
                            </div>
                        </div>
                    </div>
                    <!-- 기본요금 상세 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showBaseFee = !showBaseFee">
                            <h6 class="section-subtitle-compact">기본요금 상세</h6>
                            <div class="section-result-wrapper-compact">
                                <span class="section-result-compact">
                                    {{ formatNumber(verifyResult.baseFeeCalculation?.result, 0) }} 원
                                </span>
                                <i
                                    :class="[
                                        'fas',
                                        showBaseFee ? 'fa-chevron-up' : 'fa-chevron-down',
                                        'section-chevron-compact',
                                    ]"
                                ></i>
                            </div>
                        </div>
                        <div v-if="showBaseFee" class="calc-card-compact">
                            <!-- 입력값 표시 (Demand 또는 PeakLinked인 경우만) -->
                            <div
                                v-if="
                                    verifyResult.baseFeeCalculation?.inputs &&
                                    (verifyResult.baseFeeCalculation?.calcType === 'Demand' ||
                                        verifyResult.baseFeeCalculation?.calcType === 'PeakLinked')
                                "
                                class="calc-inputs-compact"
                            >
                                <div class="input-item-compact">
                                    <span class="input-label-compact">계약 용량:</span>
                                    <span class="input-value-compact"
                                        >{{
                                            formatNumber(verifyResult.baseFeeCalculation.inputs.contractCapacity, 2)
                                        }}
                                        kW</span
                                    >
                                </div>
                                <div
                                    v-if="verifyResult.baseFeeCalculation.inputs.maxDemand !== undefined"
                                    class="input-item-compact"
                                >
                                    <span class="input-label-compact">월간 최대수요:</span>
                                    <span class="input-value-compact"
                                        >{{
                                            formatNumber(verifyResult.baseFeeCalculation.inputs.maxDemand, 2)
                                        }}
                                        kW</span
                                    >
                                </div>
                                <div
                                    v-if="
                                        verifyResult.baseFeeCalculation.inputs.peakMaxDemand !== undefined &&
                                        verifyResult.baseFeeCalculation.inputs.peakMaxDemand > 0
                                    "
                                    class="input-item-compact"
                                >
                                    <span class="input-label-compact">피크 최대수요:</span>
                                    <span class="input-value-compact"
                                        >{{
                                            formatNumber(verifyResult.baseFeeCalculation.inputs.peakMaxDemand, 2)
                                        }}
                                        kW</span
                                    >
                                </div>
                                <div
                                    v-if="verifyResult.baseFeeCalculation.inputs.usage !== undefined"
                                    class="input-item-compact"
                                >
                                    <span class="input-label-compact">사용량:</span>
                                    <span class="input-value-compact"
                                        >{{ formatNumber(verifyResult.baseFeeCalculation.inputs.usage, 2) }}
                                        {{ billingUnit }}</span
                                    >
                                </div>
                            </div>

                            <!-- 피크 최대수요전력 상세 정보 (PeakLinked인 경우) -->
                            <div
                                v-if="
                                    verifyResult.baseFeeCalculation?.calcType === 'PeakLinked' &&
                                    verifyResult.baseFeeCalculation?.peakMaxDemandDetails
                                "
                                class="peak-details-compact"
                            >
                                <div class="peak-description-compact">
                                    {{ verifyResult.baseFeeCalculation.peakMaxDemandDetails.description }}
                                </div>
                                <div class="peak-period-compact">
                                    <strong>산정 대상 기간:</strong>
                                    {{ verifyResult.baseFeeCalculation.peakMaxDemandDetails.targetPeriod }}
                                </div>
                                <div
                                    v-if="
                                        verifyResult.baseFeeCalculation.peakMaxDemandDetails.monthlyMaxDemands &&
                                        verifyResult.baseFeeCalculation.peakMaxDemandDetails.monthlyMaxDemands.length >
                                            0
                                    "
                                    class="peak-monthly-table-compact"
                                >
                                    <table class="detail-table-compact">
                                        <thead>
                                            <tr>
                                                <th>월</th>
                                                <th class="text-right">최대수요전력 (kW)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(monthData, idx) in verifyResult.baseFeeCalculation
                                                    .peakMaxDemandDetails.monthlyMaxDemands"
                                                :key="idx"
                                                :class="{
                                                    'highlight-row':
                                                        monthData.maxDemand ===
                                                        verifyResult.baseFeeCalculation.peakMaxDemandDetails
                                                            .selectedValue,
                                                }"
                                            >
                                                <td>{{ formatYearMonth(monthData.regYm) }}</td>
                                                <td class="text-right">{{ formatNumber(monthData.maxDemand, 2) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="peak-selection-compact">
                                    <strong>선정된 값:</strong>
                                    {{
                                        formatNumber(
                                            verifyResult.baseFeeCalculation.peakMaxDemandDetails.selectedValue,
                                            2,
                                        )
                                    }}
                                    kW
                                </div>
                                <div class="peak-reason-compact">
                                    {{ verifyResult.baseFeeCalculation.peakMaxDemandDetails.selectionReason }}
                                </div>
                            </div>

                            <div class="calc-formula-compact">
                                {{ formatFormula(verifyResult.baseFeeCalculation?.formula) || "-" }}
                            </div>
                            <div class="calc-result-compact">
                                <span class="result-label-compact">결과:</span>
                                <span class="result-value-compact"
                                    >{{ formatNumber(verifyResult.baseFeeCalculation?.result, 0) }} 원</span
                                >
                            </div>
                        </div>
                    </div>

                    <!-- 과금 사용량 산정 -->
                    <div v-if="verifyResult.usage?.formula" class="calc-section-compact">
                        <div class="calc-section-header-compact">
                            <h6 class="section-subtitle-compact">과금 사용량 산정</h6>
                        </div>
                        <div class="calc-card-compact">
                            <div class="calc-formula-compact">
                                {{ formatFormula(verifyResult.usage.formula) }}
                            </div>
                        </div>
                    </div>

                    <!-- 사용량요금 상세 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showUsageFee = !showUsageFee">
                            <h6 class="section-subtitle-compact">사용량요금 상세</h6>
                            <div class="section-result-wrapper-compact">
                                <span class="section-result-compact">
                                    {{ formatNumber(verifyResult.usageFeeCalculation?.result, 0) }} 원
                                </span>
                                <i
                                    :class="[
                                        'fas',
                                        showUsageFee ? 'fa-chevron-up' : 'fa-chevron-down',
                                        'section-chevron-compact',
                                    ]"
                                ></i>
                            </div>
                        </div>
                        <div v-if="showUsageFee" class="calc-card-compact">
                            <!-- 시간대별 계산 (timeSlot) -->
                            <div
                                v-if="
                                    verifyResult.usageFeeCalculation?.feeType === 'timeSlot' &&
                                    verifyResult.usageFeeCalculation?.details &&
                                    verifyResult.usageFeeCalculation.details.length > 0
                                "
                                class="usage-details-compact"
                            >
                                <table class="detail-table-compact">
                                    <thead>
                                        <tr>
                                            <th>시간대</th>
                                            <th>사용량</th>
                                            <th>단가</th>
                                            <th>계산식</th>
                                            <th class="text-right">요금</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(detail, idx) in verifyResult.usageFeeCalculation.details"
                                            :key="idx"
                                        >
                                            <td>{{ getTimeSlotName(detail.timeSlotCode) }}</td>
                                            <td class="text-right">
                                                {{ formatNumber(detail.rawUsage, 0) }}
                                                {{ verifyResult.usage?.rawUnit || "-" }}
                                            </td>
                                            <td class="text-right">{{ formatNumber(detail.unitPrice, 0) }} 원</td>
                                            <td class="formula-cell-compact">{{ formatFormula(detail.formula) }}</td>
                                            <td class="text-right result-cell-compact">
                                                {{ formatNumber(detail.fee, 0) }} 원
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <!-- 단순 계산 (flat) -->
                            <div
                                v-else-if="
                                    verifyResult.usageFeeCalculation?.feeType === 'flat' &&
                                    verifyResult.usageFeeCalculation?.details &&
                                    verifyResult.usageFeeCalculation.details.length > 0
                                "
                                class="usage-details-compact"
                            >
                                <div
                                    v-for="(detail, idx) in verifyResult.usageFeeCalculation.details"
                                    :key="idx"
                                    class="flat-fee-item-compact"
                                >
                                    <div class="flat-fee-formula-compact">{{ formatFormula(detail.formula) }}</div>
                                    <div class="flat-fee-result-compact">{{ formatNumber(detail.result, 0) }} 원</div>
                                </div>
                            </div>
                            <div v-else class="calc-formula-compact">단순 계산 (요금 없음)</div>
                            <div class="calc-result-compact">
                                <span class="result-label-compact">합계:</span>
                                <span class="result-value-compact"
                                    >{{ formatNumber(verifyResult.usageFeeCalculation?.result, 0) }} 원</span
                                >
                            </div>
                        </div>
                    </div>

                    <!-- 기타요금 상세 -->
                    <div
                        v-if="
                            verifyResult.otherFeesCalculation?.details &&
                            verifyResult.otherFeesCalculation.details.length > 0
                        "
                        class="calc-section-compact"
                    >
                        <div class="calc-section-header-compact" @click="showOtherFees = !showOtherFees">
                            <h6 class="section-subtitle-compact">기타요금 상세</h6>
                            <div class="section-result-wrapper-compact">
                                <span class="section-result-compact">
                                    {{ formatNumber(verifyResult.otherFeesCalculation?.totalAmount, 0) }} 원
                                </span>
                                <i
                                    :class="[
                                        'fas',
                                        showOtherFees ? 'fa-chevron-up' : 'fa-chevron-down',
                                        'section-chevron-compact',
                                    ]"
                                ></i>
                            </div>
                        </div>
                        <div v-if="showOtherFees" class="calc-card-compact">
                            <div
                                class="etc-fees-list-compact"
                                :style="{
                                    gridTemplateColumns:
                                        verifyResult.otherFeesCalculation.details.length === 1
                                            ? '1fr'
                                            : verifyResult.otherFeesCalculation.details.length === 2
                                              ? 'repeat(2, 1fr)'
                                              : 'repeat(3, 1fr)',
                                }"
                            >
                                <div
                                    v-for="(detail, idx) in verifyResult.otherFeesCalculation.details"
                                    :key="detail.detailIdx || idx"
                                    class="etc-fee-item-compact"
                                >
                                    <div class="etc-fee-header-compact">
                                        <div class="etc-fee-name-group-compact">
                                            <span class="etc-fee-name-compact">{{ detail.etcCostName }}</span>
                                            <span
                                                class="etc-fee-type-badge-compact"
                                                :class="detail.calcType?.toLowerCase()"
                                            >
                                                {{
                                                    detail.calcType === "USAGE"
                                                        ? "사용량"
                                                        : detail.calcType === "FIXED"
                                                          ? "고정"
                                                          : detail.calcType
                                                }}
                                            </span>
                                        </div>
                                        <span class="etc-fee-amount-compact"
                                            >{{ formatNumber(detail.result, 0) }} 원</span
                                        >
                                    </div>
                                    <div class="etc-fee-formula-compact">{{ formatFormula(detail.formula) }}</div>
                                    <div v-if="detail.applyStartDt" class="etc-fee-period-compact">
                                        적용기간: {{ formatDate(detail.applyStartDt) }}
                                        <span v-if="detail.applyEndDt"> ~ {{ formatDate(detail.applyEndDt) }}</span>
                                        <span v-else> ~ 현재</span>
                                    </div>
                                </div>
                            </div>
                            <div class="calc-result-compact">
                                <span class="result-label-compact">합계:</span>
                                <span class="result-value-compact"
                                    >{{ formatNumber(verifyResult.otherFeesCalculation?.totalAmount, 0) }} 원</span
                                >
                            </div>
                        </div>
                    </div>

                    <!-- 소계 상세 -->
                    <div class="calc-section-compact highlight-compact">
                        <div class="calc-section-header-compact" @click="showSubTotal = !showSubTotal">
                            <h6 class="section-subtitle-compact">소계 상세</h6>
                            <div class="section-result-wrapper-compact">
                                <span class="section-result-compact highlight-result-compact">
                                    {{ formatNumber(verifyResult.subTotalCalculation?.result, 0) }} 원
                                </span>
                                <i
                                    :class="[
                                        'fas',
                                        showSubTotal ? 'fa-chevron-up' : 'fa-chevron-down',
                                        'section-chevron-compact',
                                    ]"
                                ></i>
                            </div>
                        </div>
                        <div v-if="showSubTotal" class="calc-card-compact">
                            <div class="calc-formula-compact">
                                {{ formatFormula(verifyResult.subTotalCalculation?.formula) || "-" }}
                            </div>
                            <div class="components-compact-inline">
                                <div class="component-item-compact">
                                    <span class="component-label-compact">기본요금</span>
                                    <span class="component-value-compact"
                                        >{{
                                            formatNumber(verifyResult.subTotalCalculation?.components?.baseFee, 0)
                                        }}
                                        원</span
                                    >
                                </div>
                                <div class="component-item-compact">
                                    <span class="component-label-compact">사용량요금</span>
                                    <span class="component-value-compact"
                                        >{{
                                            formatNumber(verifyResult.subTotalCalculation?.components?.usageFee, 0)
                                        }}
                                        원</span
                                    >
                                </div>
                                <div class="component-item-compact">
                                    <span class="component-label-compact">기타요금</span>
                                    <span class="component-value-compact"
                                        >{{
                                            formatNumber(verifyResult.subTotalCalculation?.components?.etcFee, 0)
                                        }}
                                        원</span
                                    >
                                </div>
                            </div>
                            <div class="calc-result-compact highlight-result-compact">
                                <span class="result-label-compact">소계:</span>
                                <span class="result-value-compact"
                                    >{{ formatNumber(verifyResult.subTotalCalculation?.result, 0) }} 원</span
                                >
                            </div>
                        </div>
                    </div>

                    <!-- 할인 및 부가세 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showDiscountVat = !showDiscountVat">
                            <h6 class="section-subtitle-compact">할인 및 부가세</h6>
                            <div class="section-result-wrapper-compact">
                                <span class="section-result-compact">
                                    할인: -{{ formatNumber(verifyResult.discountCalculation?.result, 0) }} 원 / 부가세:
                                    {{ formatNumber(verifyResult.vatCalculation?.result, 0) }} 원
                                </span>
                                <i
                                    :class="[
                                        'fas',
                                        showDiscountVat ? 'fa-chevron-up' : 'fa-chevron-down',
                                        'section-chevron-compact',
                                    ]"
                                ></i>
                            </div>
                        </div>
                        <div v-if="showDiscountVat" class="calc-card-compact">
                            <div class="discount-vat-compact">
                                <div class="discount-vat-item-inline-compact">
                                    <span class="discount-vat-label-inline-compact">할인</span>
                                    <span class="discount-vat-value-inline-compact discount-compact"
                                        >-{{ formatNumber(verifyResult.discountCalculation?.result, 0) }} 원</span
                                    >
                                    <span class="discount-vat-formula-inline-compact">
                                        {{ formatFormula(verifyResult.discountCalculation?.formula) || "-" }}
                                    </span>
                                </div>
                                <div class="discount-vat-item-inline-compact">
                                    <span class="discount-vat-label-inline-compact"
                                        >부가세 ({{
                                            formatNumber((verifyResult.vatCalculation?.vatRate || 0) * 100, 0)
                                        }}%)</span
                                    >
                                    <span class="discount-vat-value-inline-compact"
                                        >{{ formatNumber(verifyResult.vatCalculation?.result, 0) }} 원</span
                                    >
                                    <span class="discount-vat-formula-inline-compact">
                                        {{ formatFormula(verifyResult.vatCalculation?.formula) || "-" }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 최종 요금 -->
                    <div class="calc-section-compact final-section-compact">
                        <div class="calc-section-header-compact" @click="showFinalFee = !showFinalFee">
                            <h6 class="section-subtitle-compact">최종 요금</h6>
                            <div class="section-result-wrapper-compact">
                                <span class="section-result-compact final-result-compact">
                                    {{ formatNumber(verifyResult.finalCalculation?.result, 0) }} 원
                                </span>
                                <i
                                    :class="[
                                        'fas',
                                        showFinalFee ? 'fa-chevron-up' : 'fa-chevron-down',
                                        'section-chevron-compact',
                                    ]"
                                ></i>
                            </div>
                        </div>
                        <div v-if="showFinalFee" class="calc-card-compact final-card-compact">
                            <div class="calc-formula-compact">
                                {{ formatFormula(verifyResult.finalCalculation?.formula) || "-" }}
                            </div>
                            <div v-if="verifyResult.finalCalculation?.minFeeApplied" class="calc-warning-compact">
                                <i class="fas fa-info-circle"></i>
                                <span
                                    >최소 요금 적용됨:
                                    {{ formatNumber(verifyResult.finalCalculation?.minFee, 0) }} 원</span
                                >
                            </div>
                            <div class="calc-result-compact final-result-compact">
                                <span class="result-label-compact">총 요금:</span>
                                <span class="result-value-compact"
                                    >{{ formatNumber(verifyResult.finalCalculation?.result, 0) }} 원</span
                                >
                            </div>
                        </div>
                    </div>

                    <!-- 원본 데이터 -->
                    <div class="raw-data-section-compact">
                        <div class="raw-data-header-compact">
                            <h6 class="section-subtitle-compact">원본 데이터 (시간대별 사용량)</h6>
                            <button class="btn-toggle-compact" @click="showRawData = !showRawData">
                                <i :class="['fas', showRawData ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showRawData ? "접기" : "펼치기" }}
                            </button>
                        </div>

                        <div
                            v-if="showRawData && verifyResult.usage?.rawData?.hourlyUsage"
                            class="raw-data-content-compact"
                        >
                            <div class="raw-data-stats-compact">
                                <span>총 {{ verifyResult.usage.rawData.hourlyUsage.length }} 개 데이터</span>
                                <span>
                                    총 사용량:
                                    {{
                                        `${formatNumber(verifyResult.usage.rawUsage, 0) || "-"} ${verifyResult.usage.rawUnit || ""}`
                                    }}
                                </span>
                                <span>
                                    청구 사용량:
                                    {{
                                        `${formatNumber(verifyResult.usage.billingUsage, 0) || "-"} ${verifyResult.usage.billingUnit || ""}`
                                    }}
                                </span>
                                <span>
                                    기간:
                                    {{ formatDate(verifyResult.usage.calculationPeriod.startDate) }} ~
                                    {{ formatDate(verifyResult.usage.calculationPeriod.endDate) }}
                                </span>
                            </div>
                            <div class="raw-data-table-wrapper-compact">
                                <table class="raw-data-table-compact">
                                    <thead>
                                        <tr>
                                            <th>날짜</th>
                                            <th>시간</th>
                                            <th>사용량</th>
                                            <th>청구 사용량</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(data, idx) in verifyResult.usage.rawData.hourlyUsage" :key="idx">
                                            <td>{{ formatDate(data.date) }}</td>
                                            <td class="text-center">{{ data.hour }}시</td>
                                            <td class="text-right">
                                                {{ `${formatNumber(data.rawUsage, 0) || "-"}${data.rawUnit || ""}` }}
                                            </td>
                                            <td class="text-right">
                                                {{
                                                    `${formatNumber(data.billingUsage, 0) || "-"}${data.billingUnit || ""}`
                                                }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Placeholder: 검증 실행 전 -->
                <div v-else class="placeholder-section">
                    <div class="placeholder-content">
                        <i class="fas fa-file-invoice-dollar"></i>
                        <p>검증 실행 후<br />상세 정보가 표시됩니다.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi.js";

export default {
    name: "BillVerification",
    directives: {
        clickOutside: {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
    data() {
        return {
            verifyParams: {
                contractIdx: "",
                regYmMonth: "",
                includeRawData: 1,
            },
            verifying: false,
            verifyResult: null,
            contracts: [],
            showRawData: false,
            showBaseFee: true,
            showUsageFee: true,
            showOtherFees: true,
            showSubTotal: true,
            showDiscountVat: true,
            showFinalFee: true,
            // 날짜 선택기
            showDatePicker: false,
            dateYear: new Date().getFullYear(),
            dateMonth: new Date().getMonth() + 1,
        };
    },
    computed: {
        dateDisplay() {
            if (this.verifyParams.regYmMonth) {
                const [year, month] = this.verifyParams.regYmMonth.split("-");
                return `${year}년 ${parseInt(month)}월`;
            }
            return `${this.dateYear}년 ${this.dateMonth}월`;
        },
        rawUnit() {
            return this.verifyResult.usage.rawUnit || "";
        },
        billingUnit() {
            return this.verifyResult.usage.billingUnit || "";
        },
        demandUnit() {
            return this.rawUnit === "kWh" ? "kW" : "";
        },
    },
    mounted() {
        this.loadContracts();
        this.setDefaultYearMonth();
    },
    // 설정 모달 keep-alive: 다른 탭에서 바뀐 목록만 다시 받고 입력·결과는 유지
    activated() {
        if (!this.wasDeactivated) return;
        this.loadContracts();
    },
    deactivated() {
        this.wasDeactivated = true;
    },
    methods: {
        async loadContracts() {
            try {
                const result = await backEndApi.energyBillCalc.getContractList();
                if (result.status === 200) {
                    this.contracts = result.data.filter((c) => c.enabled === "Y");
                }
            } catch (error) {
                console.error("계약 목록 조회 실패:", error);
            }
        },

        setDefaultYearMonth() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, "0");
            this.verifyParams.regYmMonth = `${year}-${month}`;
            // 날짜 선택기 초기화
            this.dateYear = year;
            this.dateMonth = now.getMonth() + 1;
        },
        // 날짜 선택기 메서드
        toggleDatePicker() {
            this.showDatePicker = !this.showDatePicker;
            if (this.showDatePicker && this.verifyParams.regYmMonth) {
                const [year, month] = this.verifyParams.regYmMonth.split("-");
                this.dateYear = parseInt(year);
                this.dateMonth = parseInt(month);
            }
        },
        closeDatePicker() {
            this.showDatePicker = false;
        },
        changeYear(direction) {
            this.dateYear += direction;
            this.updateDate();
        },
        selectMonth(month) {
            this.dateMonth = month;
            this.showDatePicker = false;
            this.updateDate();
        },
        updateDate() {
            const year = String(this.dateYear);
            const month = String(this.dateMonth).padStart(2, "0");
            this.verifyParams.regYmMonth = `${year}-${month}`;
        },

        convertToYYYYMM(ymMonth) {
            if (!ymMonth) return "";
            return ymMonth.replace("-", "");
        },

        async executeVerification() {
            if (!this.verifyParams.contractIdx) {
                await this.alertWarning("계약을 선택해주세요.");
                return;
            }

            if (!this.verifyParams.regYmMonth) {
                await this.alertWarning("집계년월을 선택해주세요.");
                return;
            }

            this.verifying = true;
            this.verifyResult = null;

            try {
                const params = {
                    contractIdx: this.verifyParams.contractIdx,
                    regYm: this.convertToYYYYMM(this.verifyParams.regYmMonth),
                    includeRawData: this.verifyParams.includeRawData,
                };

                const result = await backEndApi.energyBillCalc.verifyBillCalculation(params);

                if (result.status === 200) {
                    this.verifyResult = result.data;
                    this.$bvToast.toast("요금 계산 검증이 완료되었습니다.", {
                        title: "완료",
                        variant: "info",
                        solid: true,
                    });
                } else {
                    await this.alertWarning(result.data.message || "검증에 실패했습니다.");
                }
            } catch (error) {
                console.error("요금 검증 실패:", error);
                await this.alertWarning("요금 계산 검증에 실패했습니다.");
            } finally {
                this.verifying = false;
            }
        },

        formatInputs(inputs) {
            if (!inputs) return "";
            const parts = [];
            if (inputs.contractCapacity !== undefined)
                parts.push(`계약용량: ${inputs.contractCapacity}${this.demandUnit}`);
            if (inputs.maxDemand !== undefined) parts.push(`최대수요: ${inputs.maxDemand}${this.demandUnit}`);
            if (inputs.totalUsage !== undefined) parts.push(`총사용량: ${inputs.totalUsage}${this.rawUnit}`);
            return parts.join(", ");
        },

        formatYearMonth(ym) {
            if (!ym) return "-";
            return `${ym.substring(0, 4)}년 ${ym.substring(4, 6)}월`;
        },

        formatDate(dateStr) {
            if (!dateStr) return "-";
            // 이미 YYYY-MM-DD 형식인 경우 그대로 반환
            if (dateStr.includes("-")) return dateStr;
            // YYYYMMDD 형식인 경우 변환
            if (dateStr.length === 8) {
                return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
            }
            return dateStr;
        },

        // 계약 선택 드롭다운 표시 텍스트 생성
        getContractDisplayText(contract) {
            if (!contract) return "-";

            const contractName = contract.contractName || "-";
            const costName = contract.cost?.costName || "-";

            // 유효기간 포맷팅
            let periodText = "";
            if (contract.startDate) {
                const startDate = this.formatDate(contract.startDate);
                if (contract.endDate) {
                    const endDate = this.formatDate(contract.endDate);
                    periodText = ` [${startDate} ~ ${endDate}]`;
                } else {
                    periodText = ` [${startDate} ~ 진행중]`;
                }
            }

            return `${contractName} (${costName})${periodText}`;
        },

        formatNumber(value, decimals = 2) {
            return new Intl.NumberFormat("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(value || 0);
        },
        // formula 문자열의 숫자에 천단위 콤마 추가
        formatFormula(formula) {
            if (!formula || typeof formula !== "string") return formula;
            // 숫자(소수점 포함)를 찾아서 천단위 콤마 추가
            // 단, 이미 콤마가 있는 숫자는 제외하고, 소수점이 있는 경우도 처리
            return formula.replace(/(\d+\.?\d*)/g, (match) => {
                const num = parseFloat(match);
                if (isNaN(num)) return match;
                // 소수점이 있으면 소수점 자리수 유지
                if (match.includes(".")) {
                    const decimals = match.split(".")[1]?.length || 0;
                    return this.formatNumber(num, decimals);
                }
                // 정수는 소수점 없이
                return this.formatNumber(num, 0);
            });
        },
        // 시간대 코드를 이름으로 변환
        getTimeSlotName(code) {
            const names = {
                LOW: "경부하",
                MID: "중부하",
                PEAK: "최대부하",
            };
            return names[code] || code;
        },
        // 시간대별 데이터 표시 형식: 계측 데이터 수 / 기대 데이터 수 (백분율%)
        getHourlyDataDisplay(usage) {
            if (!usage) return "0 / 0 (0%)";

            const actualCount = usage.hourlyUsageCount || 0;

            // 기대 데이터 수 계산 (계산 기간의 일수 * 24시간)
            let expectedCount = 0;
            if (usage.calculationPeriod?.startDate && usage.calculationPeriod?.endDate) {
                const startDate = this.parseDateString(usage.calculationPeriod.startDate);
                const endDate = this.parseDateString(usage.calculationPeriod.endDate);

                if (startDate && endDate) {
                    // 일수 계산 (종료일 포함)
                    const diffTime = endDate.getTime() - startDate.getTime();
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
                    expectedCount = diffDays * 24;
                }
            }

            // 백분율 계산
            const percentage = expectedCount > 0 ? ((actualCount / expectedCount) * 100).toFixed(1) : 0;

            return `${actualCount} / ${expectedCount} (${percentage}%)`;
        },
        // 날짜 문자열 파싱 (YYYYMMDD 형식)
        parseDateString(dateStr) {
            if (!dateStr || dateStr.length !== 8) return null;
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(4, 6)) - 1; // 월은 0부터 시작
            const day = parseInt(dateStr.substring(6, 8));
            return new Date(year, month, day);
        },
        // 기본요금 산정 방법 설명 (핵심 키워드만)
        getBaseFeeCalcMethod(cost) {
            if (!cost || !cost.baseFeeCalcType) return "-";

            const methodMap = {
                Fixed: "정액",
                Contract: "계약 전력",
                Demand: "최대 수요",
                Progressive: "누진제",
                PeakLinked: "피크연동제",
                None: "없음",
            };

            const methodName = methodMap[cost.baseFeeCalcType] || cost.baseFeeCalcType;
            const keywords = [methodName];

            // 추가 정보가 있으면 키워드 추가
            if (cost.baseFeeValue !== undefined && cost.baseFeeValue !== null) {
                if (cost.baseFeeCalcType === "Fixed") {
                    keywords.push(`${this.formatNumber(cost.baseFeeValue, 0)} 원`);
                } else if (
                    cost.baseFeeCalcType === "Contract" ||
                    cost.baseFeeCalcType === "Demand" ||
                    cost.baseFeeCalcType === "PeakLinked"
                ) {
                    keywords.push(`${this.formatNumber(cost.baseFeeValue, 0)} 원/kW`);
                }
            }

            return keywords.join(" · ");
        },
        // 사용료 산정 방법 설명 (핵심 키워드만)
        getUsageFeeCalcMethod(cost) {
            if (!cost) return "-";

            const keywords = [];

            if (cost.hasFlatCost === 1) {
                keywords.push("단일 요금제");
                if (cost.flatCostFee !== undefined && cost.flatCostFee !== null) {
                    keywords.push(`${this.formatNumber(cost.flatCostFee, 2)} 원/${cost.billingUnit || "kWh"}`);
                }
            } else if (cost.hasProgressive === 1) {
                keywords.push("누진 요금제");
            } else if (cost.hasTimeSlot === 1 || cost.hasSeason === 1 || cost.hasDayType === 1) {
                const timeSlotKeywords = [];
                if (cost.hasTimeSlot === 1) timeSlotKeywords.push("시간대별");
                if (cost.hasSeason === 1) timeSlotKeywords.push("계절별");
                if (cost.hasDayType === 1) timeSlotKeywords.push("요일별");
                keywords.push(timeSlotKeywords.join(" · "));
            } else {
                keywords.push("단순 요금제");
            }

            return keywords.length > 0 ? keywords.join(" · ") : "-";
        },
        // GAS 에너지 유형 여부 확인
        isGasEnergyType(supplier) {
            if (!supplier) return false;
            const energySourceCode = supplier.energySourceCode || "";
            return energySourceCode.toUpperCase() === "GAS";
        },
        // Demand 필드 표시 여부 (Demand 또는 PeakLinked이면서 가스가 아닌 경우)
        shouldShowDemandFields(verifyResult) {
            const calcType = verifyResult?.baseFeeCalculation?.calcType;
            const isGas = this.isGasEnergyType(verifyResult?.contract?.supplier);

            // 가스인 경우 PeakLinked는 표시하지 않음
            if (isGas && calcType === "PeakLinked") {
                return false;
            }

            // Demand 또는 PeakLinked일 때만 표시
            return calcType === "Demand" || calcType === "PeakLinked";
        },
        // Peak 필드 표시 여부 (PeakLinked이면서 가스가 아닌 경우)
        shouldShowPeakFields(verifyResult) {
            const calcType = verifyResult?.baseFeeCalculation?.calcType;
            const isGas = this.isGasEnergyType(verifyResult?.contract?.supplier);

            // PeakLinked이면서 가스가 아닐 때만 표시
            return calcType === "PeakLinked" && !isGas;
        },
    },
};
</script>

<style scoped>
.bill-verification {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* 헤더 */
.content-header {
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
}

.content-header h3 {
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.content-header h3 i {
    color: var(--ebc-primary);
}

/* 헤더 안의 검증 폼 */
.verify-form-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    justify-content: flex-end;
}

.verify-form-header .form-field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.verify-form-header .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    margin: 0;
}

.form-select-header {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 150px;
    height: 32px;
}

.form-select-header.contract-select-wide {
    min-width: 400px;
    max-width: 600px;
}

.form-select-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.date-picker-container-header {
    position: relative;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    z-index: 100;
    min-width: 120px;
    height: 32px;
}

.date-display-header {
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-width: 120px;
    height: 100%;
    box-sizing: border-box;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 12px;
}

.date-display-header:hover {
    background: var(--ebc-track);
}

.date-display-header i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display-header i.rotate {
    transform: rotate(180deg);
}

.btn-verify-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-verify-header:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-verify-header:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
    transform: none;
}

/* 2컬럼 레이아웃 */
.verification-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: start;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    height: 100%;
}

/* 왼쪽 패널 */
.left-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 0;
    align-self: start;
    height: 100%;
    overflow-y: auto;
}

/* 검증 폼 (컴팩트) */
.verify-form-compact {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: flex-end;
    box-shadow: var(--ebc-shadow-panel);
}

.verify-form-compact .form-field {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.verify-form-compact .form-field:last-child {
    flex: 0 0 auto;
}

.verify-form-compact .form-field label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.required {
    color: var(--ebc-danger);
}

.form-input,
.form-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 13px;
    transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.btn-verify {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-lg);
    padding: 0 20px;
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
}

.btn-verify:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-verify:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
    transform: none;
}

/* 기본 정보 컴팩트 */
.info-section-compact {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    margin-bottom: 12px;
}

.section-title-compact {
    margin: 0 0 10px 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    padding-bottom: 6px;
    border-bottom: 1px solid var(--ebc-border);
}

.info-grid-compact {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.info-row-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
}

.info-label-compact {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.info-value-compact {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text);
    text-align: right;
}

.unit-conversion-compact {
    font-size: 10px;
    color: var(--ebc-text-sub);
    margin-left: 4px;
}

.validation-warning-compact {
    color: var(--ebc-warn);
    margin-left: 4px;
    font-size: 10px;
    cursor: help;
}

/* 요금 요약 테이블 컴팩트 */
.fee-summary-table-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 12px;
    overflow: hidden;
}

.fee-table-compact {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
}

.fee-table-compact tbody tr {
    border-bottom: 1px solid var(--ebc-track);
}

.fee-table-compact tbody tr:last-child {
    border-bottom: none;
}

.fee-label-compact {
    padding: 8px 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    font-size: 11px;
}

.fee-value-compact {
    padding: 8px 10px;
    text-align: right;
    font-weight: 700;
    color: var(--ebc-text);
    font-size: 12px;
    /* font-family: "Courier New", monospace; */
}

.fee-value-compact.discount-compact {
    color: var(--ebc-danger);
}

.fee-total-row-compact {
    background: var(--ebc-positive);
}

.fee-total-row-compact .fee-label-compact,
.fee-total-row-compact .fee-value-compact {
    color: white;
    font-size: 12px;
}

.fee-total-row-compact .fee-value-compact.total-compact {
    font-size: 14px;
    font-weight: 700;
}

/* Placeholder */
.placeholder-section {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 40px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    box-shadow: var(--ebc-shadow-panel);
}

.placeholder-content {
    text-align: center;
    color: var(--ebc-text-muted);
}

.placeholder-content i {
    font-size: 28px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.placeholder-content p {
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
}

/* 오른쪽 패널 */
.right-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
    overflow: hidden;
    height: 100%;
}

/* 검증 결과 */
.verification-result {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    padding-right: 8px;
}

/* 정보 섹션 */
.info-section {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    box-shadow: var(--ebc-shadow-panel);
}

.section-title {
    margin: 0 0 16px 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
    padding-bottom: 10px;
    border-bottom: 1px solid var(--ebc-hairline);
}

.section-subtitle {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-border);
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 12px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
}

.info-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.info-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--ebc-text);
    text-align: right;
}

.unit-conversion {
    font-size: 12px;
    color: var(--ebc-text-sub);
    margin-left: 4px;
}

.validation-warning {
    color: var(--ebc-warn);
    margin-left: 6px;
}

.max-demand-warning {
    margin-top: 12px;
    padding: 12px;
    background: var(--ebc-warn-tint);
    border: 1px solid var(--ebc-warn);
    border-radius: var(--ebc-radius-xs);
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.max-demand-warning i {
    color: var(--ebc-warn);
    font-size: 16px;
    margin-top: 2px;
}

.warning-content {
    flex: 1;
}

.warning-item {
    font-size: 12px;
    color: var(--ebc-warn);
    line-height: 1.5;
    margin-bottom: 4px;
}

.warning-item:last-child {
    margin-bottom: 0;
}

.max-demand-warning-compact {
    padding: 10px 12px;
    background: var(--ebc-warn-tint);
    border: 1px solid var(--ebc-warn);
    border-radius: var(--ebc-radius-xs);
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.max-demand-warning-compact i {
    color: var(--ebc-warn);
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0;
}

.warning-content-compact {
    flex: 1;
}

.warning-item-compact {
    font-size: 11px;
    color: var(--ebc-warn);
    line-height: 1.5;
    margin-bottom: 4px;
}

.warning-item-compact:last-child {
    margin-bottom: 0;
}

/* 요금 상세 섹션 */
.fee-details-section {
    background: white;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    padding: 20px;
}

/* 계산 섹션 컴팩트 */
.calc-section-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px 12px;
    margin-bottom: 10px;
}

.calc-section-compact:last-child {
    margin-bottom: 0;
}

.calc-section-compact.highlight-compact {
    border-left: 3px solid var(--ebc-warn);
    padding-left: 10px;
}

.calc-section-compact.final-section-compact {
    border-left: 3px solid var(--ebc-positive);
    padding-left: 10px;
}

.calc-section-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
}

.section-subtitle-compact {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    padding: 0;
    border: none;
}

.section-result-wrapper-compact {
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-result-compact {
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
}

.section-result-compact.discount-compact {
    color: var(--ebc-danger);
}

.section-result-compact.highlight-result-compact {
    color: var(--ebc-warn);
}

.section-result-compact.final-result-compact {
    color: var(--ebc-positive);
    font-size: 14px;
}

.section-chevron-compact {
    font-size: 10px;
    color: var(--ebc-text-sub);
    transition: transform 0.2s ease;
}

.section-result-group-compact {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.btn-toggle-compact {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--ebc-text-sub);
}

.btn-toggle-compact:hover {
    background: var(--ebc-surface-sunken);
    border-color: var(--ebc-primary);
    color: var(--ebc-positive);
}

/* 계산 카드 컴팩트 */
.calc-card-compact {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px;
    margin-top: 8px;
}

.calc-card-compact.final-card-compact {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-primary);
}

.calc-formula-compact {
    font-size: 10px;
    color: var(--ebc-text-sub);
    /* font-family: "Courier New", monospace; */
    padding: 6px 8px;
    background: var(--ebc-warn-tint);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 8px;
    line-height: 1.4;
}

.calc-result-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    background: white;
    border-radius: var(--ebc-radius-xs);
    margin-top: 8px;
    font-size: 11px;
}

.calc-result-compact.highlight-result-compact {
    background: var(--ebc-warn-tint);
    border: 1px solid var(--ebc-warn);
    font-weight: 700;
}

.calc-result-compact.final-result-compact {
    background: var(--ebc-positive);
    color: white;
    font-size: 12px;
    font-weight: 700;
    padding: 10px;
}

.calc-result-compact.final-result-compact .result-label-compact,
.calc-result-compact.final-result-compact .result-value-compact {
    color: white;
}

.result-label-compact {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.result-value-compact {
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    /* font-family: "Courier New", monospace; */
}

/* 입력값 표시 */
.calc-inputs-compact {
    margin-bottom: 12px;
    padding: 8px 10px;
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.input-item-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 10px;
}

.input-item-compact:not(:last-child) {
    border-bottom: 1px solid var(--ebc-border);
    margin-bottom: 4px;
    padding-bottom: 6px;
}

.input-label-compact {
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.input-value-compact {
    font-weight: 700;
    color: var(--ebc-text);
}

/* 피크연동제 상세 정보 */
.peak-details-compact {
    margin-bottom: 12px;
    padding: 10px;
    background: var(--ebc-primary-tint);
    border: 1px solid var(--ebc-navy-5);
    border-radius: var(--ebc-radius-xs);
}

.peak-description-compact {
    font-size: 10px;
    color: var(--ebc-navy-2);
    margin-bottom: 8px;
    line-height: 1.5;
}

.peak-period-compact {
    font-size: 10px;
    color: var(--ebc-navy-1);
    margin-bottom: 8px;
    font-weight: 600;
}

.peak-monthly-table-compact {
    margin: 8px 0;
}

.peak-monthly-table-compact .detail-table-compact {
    font-size: 10px;
}

.peak-monthly-table-compact .detail-table-compact tbody tr.highlight-row {
    background: var(--ebc-warn-tint);
    border: 1px solid var(--ebc-warn);
}

.peak-selection-compact {
    font-size: 10px;
    color: var(--ebc-navy-1);
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--ebc-navy-5);
}

.peak-reason-compact {
    font-size: 10px;
    color: var(--ebc-navy-2);
    margin-top: 6px;
    font-style: italic;
}

.calc-warning-compact {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    background: var(--ebc-warn-tint);
    border: 1px solid var(--ebc-warn);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 8px;
    font-size: 10px;
    color: var(--ebc-warn);
}

.calc-warning-compact i {
    font-size: 11px;
}

/* 컴포넌트 컴팩트 */
.components-compact {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 8px 0;
}

.components-compact-inline {
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin: 8px 0;
    padding: 8px;
    background: white;
    border-radius: var(--ebc-radius-xs);
}

.component-row-compact {
    display: flex;
    justify-content: space-between;
    padding: 6px 8px;
    background: white;
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
}

.component-item-compact {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    padding: 6px 8px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    text-align: center;
}

.component-label-compact {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.component-value-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text);
    /* font-family: "Courier New", monospace; */
}

/* 할인 및 부가세 컴팩트 */
.discount-vat-row-compact {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.discount-vat-compact {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.discount-vat-item-inline-compact {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
}

.discount-vat-label-inline-compact {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    white-space: nowrap;
}

.discount-vat-value-inline-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text);
    /* font-family: "Courier New", monospace; */
    white-space: nowrap;
}

.discount-vat-value-inline-compact.discount-compact {
    color: var(--ebc-danger);
}

.discount-vat-formula-inline-compact {
    font-size: 10px;
    color: var(--ebc-text-muted);
    /* font-family: "Courier New", monospace; */
    flex: 1;
    text-align: right;
}

.discount-vat-item-compact {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.discount-vat-label-compact {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.discount-vat-value-compact {
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    /* font-family: "Courier New", monospace; */
}

.discount-vat-value-compact.discount-compact {
    color: var(--ebc-danger);
}

.discount-vat-formula-compact {
    font-size: 10px;
    color: var(--ebc-text-muted);
    /* font-family: "Courier New", monospace; */
    padding: 4px 6px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
}

/* 사용량 상세 테이블 컴팩트 */
.usage-details-compact {
    margin: 8px 0;
}

.detail-table-compact {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
}

.detail-table-compact thead th {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 6px 8px;
    font-weight: 600;
    text-align: left;
    color: var(--ebc-text-sub);
    font-size: 10px;
}

.detail-table-compact tbody td {
    border: 1px solid var(--ebc-border);
    padding: 6px 8px;
}

.detail-table-compact tbody td.text-right {
    text-align: right;
}

.detail-table-compact tbody td.formula-cell-compact {
    /* font-family: "Courier New", monospace; */
    font-size: 10px;
    color: var(--ebc-text-sub);
}

.detail-table-compact tbody td.result-cell-compact {
    font-weight: 700;
    color: var(--ebc-positive);
}

/* 단순 계산 (flat) 컴팩트 */
.flat-fee-item-compact {
    padding: 8px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.flat-fee-formula-compact {
    font-size: 10px;
    color: var(--ebc-text-sub);
    /* font-family: "Courier New", monospace; */
}

.flat-fee-result-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-positive);
    /* font-family: "Courier New", monospace; */
}

/* 기타요금 리스트 컴팩트 */
.etc-fees-list-compact {
    display: grid;
    gap: 8px;
    margin: 8px 0;
}

.etc-fee-item-compact {
    padding: 8px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.etc-fee-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}

.etc-fee-name-group-compact {
    display: flex;
    align-items: center;
    gap: 6px;
}

.etc-fee-name-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text);
}

.etc-fee-type-badge-compact {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 4px;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-border);
    color: var(--ebc-text-sub);
}

.etc-fee-type-badge-compact.usage,
.etc-fee-type-badge-compact.fixed {
    background: var(--ebc-track);
    color: var(--ebc-text);
}

.etc-fee-amount-compact {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-positive);
    /* font-family: "Courier New", monospace; */
}

.etc-fee-formula-compact {
    font-size: 10px;
    color: var(--ebc-text-sub);
    /* font-family: "Courier New", monospace; */
    padding: 4px 6px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 4px;
}

.etc-fee-period-compact {
    font-size: 10px;
    color: var(--ebc-text-muted);
    margin-top: 4px;
}

/* 원본 데이터 컴팩트 */
.raw-data-section-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px 12px;
}

.raw-data-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.raw-data-header-compact .section-subtitle-compact {
    margin: 0;
    padding: 0;
    border: none;
}

.raw-data-content-compact {
    margin-top: 8px;
}

.raw-data-stats-compact {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 8px 10px;
    background: var(--ebc-positive-tint);
    border: 1px solid var(--ebc-positive-tint);
    border-radius: var(--ebc-radius-xs);
    margin-bottom: 10px;
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-positive);
}

.raw-data-table-wrapper-compact {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.raw-data-table-compact {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
}

.raw-data-table-compact thead {
    position: sticky;
    top: 0;
    background: var(--ebc-surface-sunken);
    z-index: 10;
}

.raw-data-table-compact thead th {
    border: 1px solid var(--ebc-border);
    padding: 6px 8px;
    font-weight: 600;
    text-align: center;
    color: var(--ebc-text-sub);
    font-size: 10px;
}

.raw-data-table-compact tbody td {
    border: 1px solid var(--ebc-track);
    padding: 6px 8px;
}

.raw-data-table-compact tbody td.text-center {
    text-align: center;
}

.raw-data-table-compact tbody td.text-right {
    text-align: right;
    /* font-family: "Courier New", monospace; */
    font-weight: 600;
}

.raw-data-table-compact tbody tr:hover {
    background: var(--ebc-surface-sunken);
}

/* 반응형 */
@media (max-width: 1024px) {
    .verification-layout {
        grid-template-columns: 1fr;
    }
}

/* 날짜 선택기 스타일 (년월만 선택) */
.date-picker-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    padding: 0;
    z-index: 100;
    height: 100%;
    min-height: 38px;
}

.date-display {
    padding: 10px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    width: 100%;
    justify-content: center;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 13px;
    height: 100%;
    box-sizing: border-box;
}

.date-display:hover {
    background: var(--ebc-track);
}

.date-display i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display i.rotate {
    transform: rotate(180deg);
}

.date-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-md);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 101;
    margin-top: 6px;
    padding: 16px;
    min-width: 240px;
}

.year-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-border);
}

.year-selector button {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 4px 8px;
    cursor: pointer;
    color: var(--ebc-text-sub);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s ease;
    font-weight: 600;
    font-size: 12px;
}

.year-selector button:focus {
    outline: none;
}

.year-selector button:hover {
    background: var(--ebc-positive);
    color: white;
    border-color: var(--ebc-primary);
}

.year-selector span {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.month-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.month-btn {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 8px 4px;
    cursor: pointer;
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    transition: all 0.2s ease;
    text-align: center;
}

.month-btn:hover {
    background: var(--ebc-border);
    border-color: var(--ebc-border-strong);
    color: var(--ebc-text);
}

.month-btn.active {
    background: var(--ebc-positive);
    border-color: var(--ebc-primary);
    color: white;
    box-shadow: 0 1px 4px rgba(16, 185, 129, 0.3);
}
</style>
